import React, { useEffect, useState, useCallback } from "react";
import HeartLoader from "@/components/HeartLoader";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Coins, Gift, TrendingUp, Users, Sparkles, Copy, Loader2, Wand2, ArrowRight,
  Layers, Zap, X, Check, Trophy, ThumbsUp, ThumbsDown,
  Upload, KeyRound, RefreshCw, Database, ClipboardCheck, Crown, Medal, Calendar, Handshake,
  Gem, Hammer, FileText, GraduationCap, Rocket, Search as SearchIcon,
  ShieldCheck, Star, Lightbulb, Square, CheckSquare, ArrowLeftRight, Flame, Store, Heart, MessageCircle,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import UserReports from "@/components/UserReports";
import UserOutputs from "@/components/UserOutputs";
import { FairPriceBadge } from "@/components/FairPriceBadge";
import CreditNote from "@/components/CreditNote";

const KIND_META = {
  output: { I: FileText, label: "Engine output" },
  report: { I: SearchIcon, label: "Search / Ask" },
  venture: { I: Rocket, label: "Venture / App" },
  course: { I: GraduationCap, label: "Course" },
  purity: { I: ShieldCheck, label: "Product Safety scan" },
  review: { I: Star, label: "Local review" },
  insight: { I: Lightbulb, label: "Insight" },
  prompt: { I: Sparkles, label: "Prompt" },
  blueprint: { I: Layers, label: "Blueprint" },
};
const kindMeta = (k) => KIND_META[k] || { I: Gem, label: k };

const Stat = ({ icon: I, label, value, sub, testid }) => (
  <div className="rud-card rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center gap-2">
      <I className="text-brand shrink-0" size={18} />
      <div className="text-[11px] uppercase tracking-widest text-sec">{label}</div>
    </div>
    <div className="font-serif text-3xl mt-1.5">{value}</div>
    {sub && <div className="text-[11px] text-sec mt-1">{sub}</div>}
  </div>
);

export default function Prosperity() {
  const { user, setUser, fmtMoney } = useApp();
  const [s, setS] = useState(null);
  const [form, setForm] = useState({ type: "prompt", title: "", content: "", industry: "" });
  const [minting, setMinting] = useState(false);
  const [opened, setOpened] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [bounty, setBounty] = useState(null);
  const [task, setTask] = useState(null);
  const [enrich, setEnrich] = useState({ title: "", industry: "", content: "" });
  const [bountyBusy, setBountyBusy] = useState("");
  const [leaders, setLeaders] = useState([]);
  const [lbPeriod, setLbPeriod] = useState("all");
  const [lbLabel, setLbLabel] = useState("All time");
  const [apiKey, setApiKey] = useState("");
  const [mintable, setMintable] = useState(null);
  const [mintHistory, setMintHistory] = useState(null);
  const [mintingWork, setMintingWork] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [convert, setConvert] = useState({ amount: "", to: "btc" });
  const [converting, setConverting] = useState(false);
  // Mint Market
  const [market, setMarket] = useState(null);
  const [mq, setMq] = useState("");
  const [mKind, setMKind] = useState("");
  const [mSort, setMSort] = useState("trending");
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const deepAssetDone = React.useRef("");

  const loadMintable = useCallback(() => {
    api.get("/prosperity/mintable").then((r) => setMintable(r.data)).catch(() => {});
    api.get("/prosperity/mint-history").then((r) => setMintHistory(r.data)).catch(() => {});
  }, []);

  const loadMarket = useCallback(() => {
    api.get("/mint-market", { params: { q: mq, kind: mKind, sort: mSort, limit: 24 } })
      .then((r) => setMarket(r.data)).catch(() => {});
  }, [mq, mKind, mSort]);

  const load = useCallback(() => {
    if (!user) return;
    api.get("/prosperity/summary").then((r) => setS(r.data)).catch(() => {});
    loadMintable();
    api.get("/bounties/summary").then((r) => setBounty(r.data)).catch(() => {});
    api.get("/bounties/verify/next").then((r) => setTask(r.data.task)).catch(() => {});
    api.get("/me/api-key").then((r) => setApiKey(r.data.api_key || "")).catch(() => {});
  }, [user, loadMintable]);
  useEffect(load, [load]);
  useEffect(() => { if (user) loadMarket(); }, [user, loadMarket]);

  useEffect(() => {
    api.get("/leaderboard", { params: { period: lbPeriod } })
      .then((r) => { setLeaders(r.data.leaders || []); setLbLabel(r.data.label || ""); })
      .catch(() => {});
  }, [lbPeriod, user]);

  useEffect(() => {
    if (!opened) return;
    const onKey = (e) => { if (e.key === "Escape") setOpened(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [opened]);

  const copy = async (text, label) => { try { await navigator.clipboard.writeText(text); toast.success(label); } catch { toast.error("Copy failed"); } };
  const referralLink = s?.referral?.code ? `${window.location.origin}/auth?ref=${s.referral.code}` : "";

  const pending = (mintable?.items || []).filter((i) => !i.minted);
  const mintedItems = (mintable?.items || []).filter((i) => i.minted);
  const selKey = (it) => `${it.kind}:${it.id}`;

  const toggleSelect = (it) => setSelected((prev) => {
    const next = new Set(prev); const k = selKey(it);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const allSelected = pending.length > 0 && pending.every((i) => selected.has(selKey(i)));
  const toggleSelectAll = () => setSelected(() => allSelected ? new Set() : new Set(pending.map(selKey)));

  const mintStudio = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Add a title and content."); return; }
    setMinting(true);
    try {
      const { data } = await api.post("/mint-market/create", form);
      toast.success(`Minted! Live in the Mint Market · ${data.royalty_pct}% royalty per use, for life.`);
      setForm({ type: form.type, title: "", content: "", industry: "" });
      loadMintable(); loadMarket();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setMinting(false); }
  };

  const mintWork = async (item) => {
    setMintingWork(selKey(item));
    try {
      const { data } = await api.post("/prosperity/mint", { kind: item.kind, work_id: item.id });
      toast.success(`Minted "${item.title}" · earning ${data.royalty_pct}% royalty on every use, for life.`);
      loadMintable(); loadMarket();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setMintingWork(""); }
  };

  const mintSelected = async () => {
    const selections = pending.filter((i) => selected.has(selKey(i))).map((i) => ({ kind: i.kind, work_id: i.id }));
    if (selections.length === 0) { toast.error("Select at least one item to mint."); return; }
    setMintingWork("selected");
    try {
      const { data } = await api.post("/prosperity/mint-selected", { selections });
      toast.success(`Minted ${data.count} item${data.count === 1 ? "" : "s"} — now earning lifetime royalties.`);
      setSelected(new Set());
      loadMintable(); loadMarket();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setMintingWork(""); }
  };

  const mintAll = async () => {
    setMintingWork("all");
    try {
      const { data } = await api.post("/prosperity/mint-all");
      if (data.count === 0) toast("Everything is already minted.");
      else toast.success(`Minted ${data.count} works — all earning lifetime royalties now.`);
      setSelected(new Set());
      loadMintable(); loadMarket();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setMintingWork(""); }
  };

  const doConvert = async () => {
    const amt = Number(convert.amount);
    if (!amt || amt < 1) { toast.error("Enter an amount (min 1 credit)."); return; }
    setConverting(true);
    try {
      const { data } = await api.post("/prosperity/convert", { amount: amt, to: convert.to });
      const dest = convert.to === "btc" ? `≈ ₿${data.btc_amount}` : `$${data.net_usd}`;
      toast.success(`Converted ${data.credits_spent} cr → ${dest} (fee ${data.fee_credits} cr). Settlement is simulated.`);
      if (setUser) setUser((u) => u ? { ...u, credits: data.credits } : u);
      setConvert({ amount: "", to: convert.to });
      loadMintable();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setConverting(false); }
  };

  const verifyClaim = async (verdict) => {
    if (!task) return;
    setBountyBusy("verify");
    try {
      const { data } = await api.post("/bounties/verify", { task_id: task.id, verdict });
      toast.success(`+${data.awarded} credits for verifying`);
      if (setUser) setUser((u) => u ? { ...u, credits: data.credits } : u);
      const nxt = await api.get("/bounties/verify/next");
      setTask(nxt.data.task);
      api.get("/bounties/summary").then((r) => setBounty(r.data)).catch(() => {});
      api.get("/prosperity/summary").then((r) => setS(r.data)).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBountyBusy(""); }
  };

  const submitEnrich = async () => {
    if (enrich.content.trim().length < 200) { toast.error("Add a real case study (≥200 characters)."); return; }
    setBountyBusy("enrich");
    try {
      const { data } = await api.post("/bounties/enrich", enrich);
      toast.success(`+${data.awarded} credits for your case study`);
      if (setUser) setUser((u) => u ? { ...u, credits: data.credits } : u);
      setEnrich({ title: "", industry: "", content: "" });
      api.get("/bounties/summary").then((r) => setBounty(r.data)).catch(() => {});
      api.get("/prosperity/summary").then((r) => setS(r.data)).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBountyBusy(""); }
  };

  const rotateKey = async () => {
    try { const { data } = await api.post("/me/api-key/rotate"); setApiKey(data.api_key); toast.success("New API key generated"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openMarketItem = async (m) => {
    if (!user) { toast.error("Sign in to use."); return; }
    setBusyId(m.id);
    try {
      const { data } = await api.post(`/mint-market/${m.id}/use`);
      setOpened({ ...m, content: data.content, charged: data.charged, is_owner: data.is_owner, already: data.already_used });
      if (data.charged && typeof data.credits_remaining === "number" && setUser) setUser((u) => u ? { ...u, credits: data.credits_remaining } : u);
      if (data.charged) toast.success(`Used · ${data.charged} credits · creator earned ${data.creator_earned}`);
      api.get(`/mint-market/${m.id}/comments`).then((r) => setComments(r.data.comments || [])).catch(() => setComments([]));
      loadMarket(); loadMintable();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusyId(""); }
  };

  const likeAsset = async (m, e) => {
    if (e) e.stopPropagation();
    if (!user) { toast.error("Sign in to react."); return; }
    try {
      const { data } = await api.post(`/mint-market/${m.id}/like`);
      setMarket((prev) => prev ? { ...prev, assets: prev.assets.map((x) => x.id === m.id ? { ...x, liked: data.liked, likes: data.likes } : x) } : prev);
    } catch { /* ignore */ }
  };

  // Deep-link: /prosperity?asset=<id> opens that Mint Market asset where it's posted.
  useEffect(() => {
    const assetId = searchParams.get("asset");
    if (!assetId || !user || deepAssetDone.current === assetId) return;
    deepAssetDone.current = assetId;
    (async () => {
      let item = (market?.assets || []).find((x) => x.id === assetId);
      if (!item) {
        try { const { data } = await api.get("/mint-market", { params: { limit: 100 } }); item = (data.assets || []).find((x) => x.id === assetId); } catch { /* ignore */ }
      }
      if (item) {
        document.querySelector('[data-testid="mint-market"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
        openMarketItem(item);
      } else {
        toast.error("That item is no longer available on the Mint Market.");
      }
      const next = new URLSearchParams(searchParams); next.delete("asset"); setSearchParams(next, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user, market]);

  const addComment = async () => {
    if (!opened || !newComment.trim()) return;
    try {
      const { data } = await api.post(`/mint-market/${opened.id}/comments`, { text: newComment });
      setComments((c) => [data, ...c]);
      setNewComment("");
      loadMarket();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!user) return (
    <div className="max-w-2xl mx-auto px-5 pt-32 pb-24 text-center">
      <Sparkles className="mx-auto text-brand mb-4" size={32} />
      <h1 className="font-serif text-4xl tracking-tighter">Prosperity-Mint Money</h1>
      <p className="text-sec mt-3">Sign in to mint your work and earn lifetime royalties every time it's used.</p>
      <Link to="/auth" className="btn-primary h-11 px-6 inline-flex items-center gap-2 mt-6">Sign in <ArrowRight size={16} /></Link>
    </div>
  );

  const cash = s?.cash || {}; const ci = s?.credit_income || {}; const ref = s?.referral || {};

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Zap size={14} /> Prosperity-Mint Money</span>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-3">Mint everything you create.</h1>
        <p className="text-sec mt-2 text-sm max-w-2xl">Mint your asks, searches, reports, insights, courses, prompts, blueprints, scans & more — right here. Earn a <b className="text-brand">royalty on every single use by others, for life</b>. Rates rise with quality &amp; demand.</p>
      </motion.div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat icon={Coins} label="Credits" value={s ? Math.round(s.credits).toLocaleString() : "—"} testid="pros-credits" />
        <Stat icon={Gem} label="Mint royalties (life)" value={<span className="inline-flex items-center gap-1.5"><Coins size={18} className="text-brand" />{Math.round(ci.mints ?? 0).toLocaleString()}</span>} sub={`${mintable?.total_uses ?? 0} uses across your mints`} testid="pros-royalties" />
        <Stat icon={Gift} label="Referral earnings" value={<span className="inline-flex items-center gap-1.5"><Coins size={18} className="text-brand" />{fmtMoney(cash.referral ?? 0)}</span>} sub={<>Cash out at {fmtMoney(cash.cashout_threshold ?? 5000)}</>} testid="pros-referral" />
        <Stat icon={TrendingUp} label="Credit income" value={Math.round(ci.total ?? 0).toLocaleString()} sub={`Royalties ${Math.round(ci.mints ?? 0)} · Outputs ${Math.round(ci.outputs ?? 0)} · Reports ${Math.round(ci.reports ?? 0)}`} testid="pros-credit-income" />
      </div>

      {/* ============ MINT DASHBOARD ============ */}
      <div className="rud-card rounded-2xl p-6 mt-4" data-testid="mint-dashboard">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Hammer size={14} /> Mint dashboard</div>
            <p className="text-sm text-sec mt-1 max-w-xl">Every activity you create is collected here with its live <b className="text-brand">value</b> and how many times it's been <b className="text-brand">used</b>. Select what to mint, or mint everything at once.</p>
          </div>
          <div className="text-right shrink-0">
            <div className="font-serif text-3xl text-brand" data-testid="mintable-total">{mintable ? mintable.mintable_count : "—"}</div>
            <div className="text-[11px] text-sec">activities ready to mint · {mintable ? Math.round(mintable.mintable_total) : 0} value</div>
          </div>
        </div>

        {/* Per-activity stats */}
        {mintable && mintable.stats.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-4" data-testid="mint-stats">
            {mintable.stats.map((st) => {
              const M = kindMeta(st.kind);
              return (
                <div key={st.kind} className="rounded-xl border border-rud bg-elevated p-3" data-testid={`mint-stat-${st.kind}`}>
                  <div className="flex items-center gap-1.5 text-[11px] text-sec"><M.I size={13} className="text-brand" /> {st.label}</div>
                  <div className="font-serif text-2xl mt-1">{st.count}</div>
                  <div className="text-[10px] text-sec mt-0.5 flex flex-wrap gap-x-2">
                    <span>{st.minted} minted</span>
                    <span className="flex items-center gap-0.5"><Users size={9} /> {st.uses} uses</span>
                    <span className="text-brand">+{Math.round(st.earned)} cr</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Selection controls */}
        {mintable && mintable.mintable_count > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <button data-testid="mint-select-all" onClick={toggleSelectAll} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-2">
              {allSelected ? <CheckSquare size={15} className="text-brand" /> : <Square size={15} />} {allSelected ? "Clear all" : "Select all"}
            </button>
            <button data-testid="mint-selected-btn" onClick={mintSelected} disabled={mintingWork === "selected" || selected.size === 0}
              className="btn-primary h-9 px-5 rounded-full text-sm flex items-center gap-2 disabled:opacity-50">
              {mintingWork === "selected" ? <Loader2 size={15} className="animate-spin" /> : <Hammer size={15} />} Mint selected ({selected.size})
            </button>
            <button data-testid="mint-all-btn" onClick={mintAll} disabled={mintingWork === "all"}
              className="btn-ghost rounded-full h-9 px-5 text-sm flex items-center gap-2 disabled:opacity-50">
              {mintingWork === "all" ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} className="text-brand" />} Mint all
            </button>
          </div>
        )}

        {/* Mintable list */}
        <div className="mt-4 space-y-2 max-h-[30rem] overflow-auto pr-1" data-testid="mintable-list">
          {!mintable ? (
            <div className="grid place-items-center py-8"><HeartLoader /></div>
          ) : mintable.items.length === 0 ? (
            <div className="text-center text-sec text-sm py-8">
              No activities yet. Run a Search, generate an Engine output, scan with Product Safety, launch a Course or post a Venture — it'll appear here to mint.
              <div className="flex flex-wrap gap-2 justify-center mt-3">
                <Link to="/engine" className="btn-ghost rounded-full text-xs h-8 px-3">Open Engine</Link>
                <Link to="/search" className="btn-ghost rounded-full text-xs h-8 px-3">Run a Search</Link>
                <Link to="/learn" className="btn-ghost rounded-full text-xs h-8 px-3">Launch a Course</Link>
              </div>
            </div>
          ) : mintable.items.map((it) => {
            const M = kindMeta(it.kind);
            const isSel = selected.has(selKey(it));
            return (
              <div key={selKey(it)} className={`rounded-xl border p-3 flex items-center gap-3 ${it.minted ? "border-rud bg-elevated/40" : isSel ? "border-brand bg-brand/10" : "border-brand/30 bg-brand/5"}`} data-testid={`mintable-${it.kind}-${it.id}`}>
                {!it.minted && (
                  <button onClick={() => toggleSelect(it)} className="shrink-0 text-brand" data-testid={`mint-check-${it.id}`} aria-label="select">
                    {isSel ? <CheckSquare size={18} /> : <Square size={18} className="text-sec" />}
                  </button>
                )}
                <div className="w-9 h-9 rounded-lg bg-brand/15 grid place-items-center text-brand shrink-0"><M.I size={16} /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate flex items-center gap-1.5">{it.title}{it.trending && <Flame size={12} className="text-orange-400 shrink-0" />}</div>
                  <div className="text-[11px] text-sec flex flex-wrap items-center gap-x-2">
                    <span>{it.label}</span>
                    <span className="flex items-center gap-1"><Users size={10} /> {it.uses} uses</span>
                    <span className="text-brand">{it.royalty_pct}% royalty · {it.use_cost} cr/use</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-serif text-lg text-brand flex items-center gap-1 justify-end"><Coins size={13} /> {it.value}</div>
                  <div className="text-[10px] text-sec">value</div>
                </div>
                {it.minted ? (
                  <span className="shrink-0 text-[11px] text-brand flex flex-col items-center w-24 justify-center" data-testid={`minted-badge-${it.id}`}>
                    <span className="flex items-center gap-1"><Check size={13} /> Minted</span>
                    <span className="text-[10px] text-sec">earned {it.earned} cr</span>
                  </span>
                ) : (
                  <button data-testid={`mint-work-${it.id}`} onClick={() => mintWork(it)} disabled={!!mintingWork}
                    className="shrink-0 btn-primary h-8 px-4 rounded-full text-xs flex items-center gap-1.5 w-24 justify-center disabled:opacity-60">
                    {mintingWork === selKey(it) ? <Loader2 size={12} className="animate-spin" /> : <><Hammer size={12} /> Mint</>}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ============ CONVERT + MANUAL MINT ============ */}
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        {/* Credits stay in-app */}
        <div className="rud-card rounded-2xl p-6" data-testid="mint-convert">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Coins size={14} /> Your mint royalties</div>
            <FairPriceBadge compact />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="rounded-xl border border-rud bg-elevated p-3">
              <div className="text-[11px] text-sec">Lifetime mint royalties</div>
              <div className="font-serif text-2xl text-brand" data-testid="convert-royalties">{Math.round(mintable?.mint_royalties_lifetime ?? 0).toLocaleString()}</div>
            </div>
            <div className="rounded-xl border border-rud bg-elevated p-3">
              <div className="text-[11px] text-sec">Spendable balance</div>
              <div className="font-serif text-2xl">{Math.round(user?.credits ?? 0).toLocaleString()}</div>
            </div>
          </div>
          <p className="text-xs text-sec" data-testid="credits-in-app-note">
            Your royalties are paid as <b className="text-brand">My Kind Credits</b> and stay inside the app — they can't be converted to crypto or fiat. Spend them anywhere across My Kind: AI outputs &amp; blueprints, University courses, Community Travel, Secure Meet and more.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Link to="/engine" data-testid="spend-engine" className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><ArrowLeftRight size={14} /> Use in Engine</Link>
            <Link to="/learn" data-testid="spend-learn" className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><GraduationCap size={14} /> University</Link>
          </div>
          <div className="mt-5"><CreditNote /></div>
        </div>

        {/* Manual mint studio (prompt / blueprint) */}
        <div className="rud-card rounded-2xl p-6" data-testid="mint-studio">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><Wand2 size={14} /> Mint a prompt or blueprint</div>
          <div className="flex gap-2 mb-3">
            {["prompt", "blueprint"].map((tp) => (
              <button key={tp} data-testid={`mint-type-${tp}`} onClick={() => setForm((f) => ({ ...f, type: tp }))}
                className={`flex-1 rounded-lg p-3 border text-left transition-colors ${form.type === tp ? "border-brand bg-brand/10" : "border-rud hover:border-brand/50"}`}>
                {tp === "prompt" ? <Sparkles size={16} className="text-brand mb-1" /> : <Layers size={16} className="text-brand mb-1" />}
                <div className="text-sm font-medium capitalize">{tp}</div>
                <div className="text-[11px] text-sec">{tp === "prompt" ? "from ~12% royalty" : "from ~22% royalty"}</div>
              </button>
            ))}
          </div>
          <input data-testid="mint-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. 'FDA-ready medtech blueprint')" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mb-2" />
          <input data-testid="mint-industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="Industry / category (optional)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mb-2" />
          <textarea data-testid="mint-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={4} placeholder={form.type === "prompt" ? "Paste the prompt others can reuse…" : "Describe the blueprint / structure others can build from…"} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <button data-testid="mint-submit" onClick={mintStudio} disabled={minting} className="btn-primary h-11 px-6 mt-3 flex items-center gap-2 disabled:opacity-60">
            {minting ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} Mint {form.type}
          </button>
        </div>
      </div>

      {/* Mint history */}
      {mintHistory && mintHistory.count > 0 && (
        <div className="rud-card rounded-2xl p-6 mt-4" data-testid="mint-history">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-sec flex items-center gap-1.5"><Gem size={13} className="text-brand" /> Your minted assets</div>
            <div className="text-xs text-sec">Lifetime earned: <b className="text-brand" data-testid="mint-history-total">{mintHistory.total_earned}</b> cr · {mintHistory.total_uses} uses · {mintHistory.count} assets</div>
          </div>
          <div className="space-y-1.5 max-h-64 overflow-auto pr-1">
            {mintHistory.assets.map((h) => {
              const M = kindMeta(h.kind);
              return (
                <div key={h.id} className="flex items-center gap-3 text-sm rounded-lg border border-rud px-3 py-2" data-testid={`mint-history-${h.id}`}>
                  <M.I size={13} className="text-brand shrink-0" />
                  <span className="flex-1 truncate">{h.title}
                    {h.nudge && <span className="ml-2 text-[10px] text-orange-400 inline-flex items-center gap-0.5" data-testid={`mint-nudge-${h.id}`}><Flame size={10} /> Popular — refresh it to keep earning</span>}
                  </span>
                  <span className="text-[11px] text-sec shrink-0 flex items-center gap-1"><Users size={10} /> {h.uses}</span>
                  <span className="text-[11px] text-sec shrink-0 hidden sm:inline">{new Date(h.created_at).toLocaleDateString()}</span>
                  <span className="text-brand font-medium shrink-0">+{h.earned}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ============ MINT MARKET ============ */}
      <div className="rud-card rounded-2xl p-6 mt-4" data-testid="mint-market">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-1"><Store size={14} /> Mint Market</div>
        <p className="text-xs text-sec mb-4">Discover &amp; use what the community has minted. Using pays the creator their lifetime royalty; My Kind takes a {market?.platform_use_fee_pct ?? 15}% platform fee. Prices rise with demand.</p>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative flex-1 min-w-[180px]">
            <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-sec" />
            <input data-testid="market-search" value={mq} onChange={(e) => setMq(e.target.value)} placeholder="Search minted assets…" className="rud-input w-full rounded-full pl-9 pr-3 py-2 text-sm" />
          </div>
          <select data-testid="market-kind" value={mKind} onChange={(e) => setMKind(e.target.value)} className="rud-input rounded-full px-3 py-2 text-sm">
            <option value="">All types</option>
            {Object.entries(market?.kinds || KIND_META).map(([k, v]) => (
              <option key={k} value={k}>{typeof v === "string" ? v : v.label}</option>
            ))}
          </select>
          <div className="flex items-center gap-1 p-0.5 rounded-full bg-elevated border border-rud text-[11px]">
            {[["trending", "Trending"], ["new", "New"], ["top", "Top earning"]].map(([k, lbl]) => (
              <button key={k} data-testid={`market-sort-${k}`} onClick={() => setMSort(k)}
                className={`px-3 py-1 rounded-full transition-colors ${mSort === k ? "bg-brand text-black" : "text-sec hover:text-brand"}`}>{lbl}</button>
            ))}
          </div>
        </div>
        {!market ? (
          <div className="grid place-items-center py-8"><HeartLoader /></div>
        ) : market.assets.length === 0 ? (
          <p className="text-sm text-sec">No minted assets match yet — mint one above to be first on the market.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {market.assets.map((m) => {
              const M = kindMeta(m.kind);
              return (
                <div key={m.id} className="rounded-xl border border-rud bg-elevated p-4 flex flex-col" data-testid={`market-asset-${m.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-brand border border-brand/40 rounded-full px-2 py-0.5 flex items-center gap-1"><M.I size={11} /> {m.label}</span>
                    {m.trending && <span className="text-[10px] text-orange-400 flex items-center gap-0.5"><Flame size={11} /> hot</span>}
                    {m.category && <span className="text-[11px] text-sec truncate">{m.category}</span>}
                  </div>
                  <div className="text-sm font-medium mt-2 line-clamp-2">{m.title}</div>
                  <p className="text-xs text-sec mt-1 line-clamp-2">{m.snippet}</p>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-rud text-[11px] text-sec">
                    <span>by {m.owner_name}</span>
                    <button data-testid={`like-market-${m.id}`} onClick={(e) => likeAsset(m, e)} className={`flex items-center gap-1 transition-colors ${m.liked ? "text-brand" : "hover:text-brand"}`}>
                      <Heart size={12} fill={m.liked ? "currentColor" : "none"} /> {m.likes || 0}
                    </button>
                    <span className="flex items-center gap-1"><MessageCircle size={12} /> {m.comments || 0}</span>
                    <span className="ml-auto flex items-center gap-1"><Users size={11} /> {m.uses}</span>
                    <span className="text-brand">{m.royalty_pct}%</span>
                  </div>
                  <button data-testid={`use-market-${m.id}`} onClick={() => openMarketItem(m)} disabled={busyId === m.id}
                    className="btn-primary w-full h-9 rounded-full text-xs mt-3 flex items-center justify-center gap-1.5 disabled:opacity-60">
                    {busyId === m.id ? <Loader2 size={13} className="animate-spin" /> : <ArrowRight size={13} />} {m.owned_or_used ? "Open (free)" : `Use · ${m.use_cost} cr`}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Referral — 3 tiers */}
      <div className="rud-card rounded-2xl p-6 mt-4" data-testid="pros-referral-panel">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><Users size={14} /> 3-Tier referral network</div>
        <div className="grid sm:grid-cols-3 gap-3">
          {(ref.tiers || []).map((t) => (
            <div key={t.tier} className="rounded-xl border border-rud bg-elevated p-4">
              <div className="flex items-center justify-between"><span className="text-sm font-medium">Tier {t.tier}</span><span className="text-brand font-semibold">{t.rate}%</span></div>
              <div className="text-3xl font-bold tabular-nums mt-2">{t.count}</div>
              <div className="text-[11px] text-sec">people · earns {t.rate}% of their credit buys</div>
            </div>
          ))}
        </div>
        {referralLink && (
          <div className="flex items-center gap-2 bg-elevated rounded-md p-2 mt-4 font-mono text-xs break-all border border-rud">
            {referralLink}
            <button data-testid="pros-copy-referral" onClick={() => copy(referralLink, "Invite link copied")} className="text-brand shrink-0"><Copy size={14} /></button>
          </div>
        )}
        <p className="text-[11px] text-sec mt-2">You earn <b className="text-brand">15% / 5% / 2%</b> instantly on every credit purchase across three tiers of your network.</p>
      </div>

      <Link to="/affiliate" data-testid="pros-affiliate-link" className="flex items-center gap-3 rud-card rounded-2xl p-5 mt-4 hover:border-brand/50 transition-colors">
        <div className="w-10 h-10 rounded-xl bg-brand/15 grid place-items-center text-brand shrink-0"><Handshake size={18} /></div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm">Affiliate Partner Program</div>
          <div className="text-xs text-sec">Partners, influencers &amp; apps: share your link and earn a signup bonus on every new user.</div>
        </div>
        <ArrowRight size={16} className="text-brand shrink-0" />
      </Link>

      {/* Task Bounties */}
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <div className="rud-card rounded-2xl p-6" data-testid="bounty-verify">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-1"><ClipboardCheck size={14} /> Verify AI data · +{bounty?.verify_reward ?? 0.5} cr</div>
          <p className="text-xs text-sec mb-4">Help keep My Kind accurate. {bounty ? `${bounty.verified_today}/${bounty.verify_cap} verified today · ${bounty.available} left` : ""}</p>
          {task ? (
            <div className="rounded-xl border border-rud bg-elevated p-4">
              <div className="text-[11px] uppercase tracking-widest text-brand">{task.category}</div>
              <p className="text-sm mt-1">{task.claim}</p>
              <div className="flex gap-2 mt-4">
                <button data-testid="verify-accurate" onClick={() => verifyClaim("accurate")} disabled={!!bountyBusy} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-60"><ThumbsUp size={14} /> Accurate</button>
                <button data-testid="verify-inaccurate" onClick={() => verifyClaim("inaccurate")} disabled={!!bountyBusy} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-60"><ThumbsDown size={14} /> Inaccurate</button>
              </div>
            </div>
          ) : <p className="text-sm text-sec">🎉 You've verified everything available. Check back later.</p>}
        </div>

        <div className="rud-card rounded-2xl p-6" data-testid="bounty-enrich">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-1"><Upload size={14} /> Data enrichment · +{bounty?.enrich_reward ?? 2} cr</div>
          <p className="text-xs text-sec mb-3">Upload a verified case study to enrich My Kind. {bounty ? `${bounty.enriched_today}/${bounty.enrich_cap} today` : ""}</p>
          <input data-testid="enrich-title" value={enrich.title} onChange={(e) => setEnrich({ ...enrich, title: e.target.value })} placeholder="Case study title" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mb-2" />
          <input data-testid="enrich-industry" value={enrich.industry} onChange={(e) => setEnrich({ ...enrich, industry: e.target.value })} placeholder="Industry (optional)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mb-2" />
          <textarea data-testid="enrich-content" value={enrich.content} onChange={(e) => setEnrich({ ...enrich, content: e.target.value })} rows={4} placeholder="Paste the verified case study (facts, outcomes, numbers)…" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[11px] text-sec">{enrich.content.trim().length}/200 min</span>
            <button data-testid="enrich-submit" onClick={submitEnrich} disabled={bountyBusy === "enrich"} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-60">{bountyBusy === "enrich" ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Submit & earn</button>
          </div>
        </div>
      </div>

      {/* Creator Leaderboard */}
      <div className="rud-card rounded-2xl p-6 mt-4" data-testid="leaderboard">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Trophy size={14} /> Creator leaderboard</div>
          <div className="flex items-center gap-1 p-0.5 rounded-full bg-elevated border border-rud text-[11px]">
            {[["week", "Weekly"], ["month", "Monthly"], ["all", "All-time"]].map(([k, label]) => (
              <button key={k} data-testid={`lb-period-${k}`} onClick={() => setLbPeriod(k)}
                className={`px-3 py-1 rounded-full transition-colors ${lbPeriod === k ? "bg-brand text-black" : "text-sec hover:text-brand"}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="text-[11px] text-sec mb-3 flex items-center gap-1.5" data-testid="lb-season-label"><Calendar size={12} /> {lbLabel}</div>
        {leaders.length === 0 ? <p className="text-sm text-sec">No creator earnings {lbPeriod === "all" ? "yet" : `this ${lbPeriod === "week" ? "week" : "month"}`} — mint an activity and be the first on the board.</p> : (
          <div className="divide-y divide-[var(--rud-border,#26262c)]">
            {leaders.map((l) => {
              const badge = l.rank === 1 ? { I: Crown, cls: "text-yellow-400", label: "Champion" }
                : l.rank === 2 ? { I: Medal, cls: "text-zinc-300", label: "Runner-up" }
                : l.rank === 3 ? { I: Medal, cls: "text-amber-600", label: "Third" } : null;
              return (
                <div key={l.rank} className="flex items-center gap-3 py-2.5" data-testid={`leader-${l.rank}`}>
                  <span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold shrink-0 ${l.rank <= 3 ? "bg-brand text-black" : "bg-elevated text-sec border border-rud"}`}>{l.rank}</span>
                  <span className="font-medium text-sm truncate flex-1 flex items-center gap-1.5">{l.name}{badge && <badge.I size={14} className={badge.cls} />}</span>
                  <span className="text-[11px] text-sec hidden sm:inline">royalties {l.mint_earnings} · reports {l.report_earnings} · outputs {l.output_earnings ?? 0}</span>
                  <span className="text-brand font-semibold text-sm">{l.total} cr</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cross-app unified API */}
      <div className="rud-card rounded-2xl p-6 mt-4" data-testid="cross-app-api">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-1"><Database size={14} /> Cross-app My Kind API</div>
        <p className="text-xs text-sec mb-3">Your apps can share data & insights through one unified data bus. Use this key from any app you build.</p>
        <div className="flex items-center gap-2 bg-elevated rounded-md p-2 font-mono text-xs break-all border border-rud">
          <KeyRound size={14} className="text-brand shrink-0" />
          <span data-testid="api-key" className="flex-1">{apiKey || "—"}</span>
          <button onClick={() => copy(apiKey, "API key copied")} className="text-brand shrink-0" data-testid="copy-api-key"><Copy size={14} /></button>
          <button onClick={rotateKey} className="text-sec hover:text-brand shrink-0" title="Rotate" data-testid="rotate-api-key"><RefreshCw size={14} /></button>
        </div>
        <pre className="text-[11px] text-sec mt-3 bg-elevated border border-rud rounded-lg p-3 overflow-auto">{`POST ${window.location.origin}/api/bus/<namespace>
Header: X-My Kind-Key: <your key>
Body:   { "data": { ... } }

GET  ${window.location.origin}/api/bus/<namespace>  → your shared data`}</pre>
      </div>

      {/* Reports & outputs (Save & Earn) */}
      <div className="mt-4"><UserReports ownerId={user.user_id} isOwner title="Your saved Search & Ask reports" /></div>
      <div className="mt-4"><UserOutputs ownerId={user.user_id} isOwner title="Your saved outputs — Save & Earn" /></div>

      {/* Opened mint content modal */}
      {opened && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setOpened(null)} data-testid="mint-content-modal">
          <div className="rud-card rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div><div className="text-[10px] uppercase tracking-widest text-brand">{opened.label || opened.kind}</div><h3 className="font-serif text-2xl">{opened.title}</h3></div>
              <button onClick={() => setOpened(null)} className="text-sec hover:text-brand"><X size={20} /></button>
            </div>
            <div data-testid="mint-content-body" className="whitespace-pre-wrap text-sm leading-relaxed mt-4 bg-elevated border border-rud rounded-lg p-5 max-h-[45vh] overflow-auto">{opened.content}</div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => copy(opened.content, "Copied to clipboard")} className="btn-primary h-10 px-5 text-sm flex items-center gap-2"><Copy size={15} /> Copy</button>
              {opened.kind === "blueprint" && <Link to="/engine" className="btn-ghost rounded-full h-10 px-5 text-sm flex items-center gap-2"><Check size={15} /> Build in Engine</Link>}
            </div>
            {opened.id && (
              <div className="mt-5 border-t border-rud pt-4" data-testid="mint-comments">
                <div className="text-[11px] uppercase tracking-widest text-brand mb-2 flex items-center gap-1.5"><MessageCircle size={12} /> Community comments</div>
                <div className="flex gap-2 mb-3">
                  <input data-testid="mint-comment-input" value={newComment} onChange={(e) => setNewComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder="Add a comment…" className="rud-input flex-1 rounded-lg px-3 py-2 text-sm" />
                  <button data-testid="mint-comment-submit" onClick={addComment} className="btn-primary h-10 px-4 rounded-lg text-sm">Post</button>
                </div>
                <div className="space-y-2 max-h-48 overflow-auto">
                  {comments.length === 0 ? <p className="text-xs text-sec">Be the first to comment.</p> : comments.map((c) => (
                    <div key={c.id} className="text-sm rounded-lg border border-rud px-3 py-2">
                      <span className="text-brand text-xs">{c.user_name}</span>
                      <p className="text-sec">{c.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
